/**
 * Stage 0 — The framing screen.
 *
 * 90 seconds. Three sentences. One job: prime the user to commit to answers
 * rather than self-summarize. Without this framing, users expect a
 * paste-bio textarea and get confused when they see the gauntlet.
 *
 * This is also the only stage that asks the user for a display name for
 * their worldview, because everything downstream uses it in copy ("ask
 * your brain a question").
 */

import { type FormEvent, useEffect, useRef, useState } from 'react';

interface Stage0FrameProps {
  initialName?: string;
  onBegin: (name: string) => void;
  onCancel: () => void;
  questionCount: number;
}

export function Stage0Frame({
  initialName = '',
  onBegin,
  onCancel,
  questionCount,
}: Stage0FrameProps) {
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = window.setTimeout(() => inputRef.current?.focus(), 250);
    return () => window.clearTimeout(t);
  }, []);

  const trimmed = name.trim();
  const canBegin = trimmed.length > 0;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (canBegin) onBegin(trimmed);
  };

  return (
    <div className="onboarding-frame">
      <p className="onboarding-frame__eyebrow">— The Analyzer —</p>
      <h1 className="onboarding-frame__title">
        We won&rsquo;t ask you to describe yourself.
      </h1>

      <div className="onboarding-frame__lede">
        <p>
          Describing yourself doesn&rsquo;t work. People recite flattering
          values, then reason differently in practice.
        </p>
        <p>
          Instead we&rsquo;ll show you <em>concrete questions</em>. You
          commit to answers. From those answers we&rsquo;ll build a brain
          that reasons the way you actually do — and tests it against you
          at the end.
        </p>
      </div>

      <ul className="onboarding-frame__beats">
        <li>
          <span className="onboarding-frame__beat-num">01</span>
          <span className="onboarding-frame__beat-text">
            <strong>{questionCount} forced-choice questions.</strong> Pick
            the option closest to you, type one sentence on why. No right
            answers. About 12 minutes.
          </span>
        </li>
        <li>
          <span className="onboarding-frame__beat-num">02</span>
          <span className="onboarding-frame__beat-text">
            <strong>A test debate.</strong> Ask your brain a question
            it&rsquo;s never seen. Read what it says back.
          </span>
        </li>
        <li>
          <span className="onboarding-frame__beat-num">03</span>
          <span className="onboarding-frame__beat-text">
            <strong>Save if it sounds like you.</strong> If not, refine and
            try again. Pause anytime — we save your progress.
          </span>
        </li>
      </ul>

      <form
        className="onboarding-frame__form"
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <label className="onboarding-frame__label" htmlFor="onboarding-name">
          What should we call this brain?
        </label>
        <input
          ref={inputRef}
          id="onboarding-name"
          name="brain-name"
          type="text"
          className="onboarding-frame__input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Arthur, or The Pragmatist"
          maxLength={60}
          // `nickname` tells Firefox specifically what this field is, which
          // suppresses the heuristic that treats single-text-input forms as
          // possibly-payment-related (the source of the orange autofill
          // warning on HTTP localhost). data-* attrs suppress 1Password /
          // LastPass / Bitwarden injection.
          autoComplete="nickname"
          spellCheck={false}
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
        />

        <div className="onboarding-frame__actions">
          <button
            type="button"
            className="onboarding-frame__cancel"
            onClick={onCancel}
          >
            Not now
          </button>
          <button
            type="submit"
            className="onboarding-frame__begin"
            disabled={!canBegin}
          >
            Begin <span aria-hidden="true">→</span>
          </button>
        </div>
      </form>
    </div>
  );
}
